import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from '@/hooks/use-toast';
import type { Database } from "@/lib/types";
import { useAuth } from '@/hooks/useAuth';
import { getImpersonationContext } from '@/utils/impersonation';
const DEFAULT_ORGANIZATION_ID = 'fe3bd8b7-d5ad-4cb4-8ee6-cbf0a3a5b2d2';

type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json | undefined }
  | Json[]

type Screen = {
  id: string;
  name: string;
  organization_id: string;
  settings: Json | null;
  created_at?: string;
  updated_at?: string;
};
type ScreenInsert = Database['public']['Tables']['screens']['Insert'];
type ScreenUpdate = Database['public']['Tables']['screens']['Update'];

export const useScreens = (overrideUserId?: string, overrideOrganizationId?: string) => {
  const [screens, setScreens] = useState<Screen[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();

  const fetchScreens = async () => {
    const { targetUserId: impersonationParam } = getImpersonationContext();
    const targetUserId = overrideUserId || impersonationParam || user?.id;

    // DEBUG: Log detalhado dos parâmetros
    console.log('📱 useScreens - fetchScreens chamado:', {
      overrideUserId,
      impersonationParam,
      userIdFromAuth: user?.id,
      targetUserId,
      authLoading,
      overrideOrganizationId
    });

    if (authLoading || !targetUserId) {
      console.log('📱 useScreens - Waiting for auth to complete or no user ID');
      if (!authLoading && !user?.id) {
        setLoading(false);
      }
      return;
    }

    try {
      console.log('📱 useScreens - Fetching screens for user:', targetUserId);
      const isImpersonationMode = !!impersonationParam;

      // Se a org foi fornecida, priorize consulta direta por organização
      const orgFromUrl = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('org') || undefined : undefined;
      const effectiveOrgId = overrideOrganizationId || orgFromUrl;

      console.log('📱 useScreens - Parâmetros de organização:', {
        overrideOrganizationId,
        orgFromUrl,
        effectiveOrgId,
        isImpersonationMode
      });

      if (effectiveOrgId) {
        console.log('📱 useScreens - Buscando por organization_id:', effectiveOrgId);
        const { data, error } = await supabase
          .from('screens')
          .select('*')
          .eq('organization_id', effectiveOrgId)
          .order('created_at', { ascending: false });
        if (!error && data) {
          console.log('✅ useScreens - Telas encontradas por org:', data.length);
          setScreens((data || []).map(screen => ({
            ...screen,
            settings: typeof screen.settings === 'object' && screen.settings !== null ? screen.settings : {}
          })) as any);
          return;
        } else {
          console.warn('⚠️ useScreens - Erro ou sem dados ao buscar por org:', error);
        }
      }

      if (isImpersonationMode) {
        if (effectiveOrgId) {
          const { data, error } = await supabase
            .from('screens')
            .select('*')
            .eq('organization_id', effectiveOrgId)
            .order('created_at', { ascending: false });
          if (error) throw error;
          setScreens((data || []).map(screen => ({
            ...screen,
            settings: typeof screen.settings === 'object' && screen.settings !== null ? screen.settings : {}
          })) as any);
        } else {
          setScreens([]);
        }
      } else {
        // Normal mode: resolver organização e buscar diretamente por organization_id
        let orgId: string | null = effectiveOrgId || null;
        if (!orgId) {
          const { data: orgMap } = await supabase
            .from('organizations_users')
            .select('organization_id')
            .eq('user_id', targetUserId)
            .limit(1);
          orgId = orgMap && orgMap[0]?.organization_id ? String(orgMap[0].organization_id) : null;
          if (!orgId) {
            const { data: devOrg } = await supabase
              .from('devices')
              .select('organization_id')
              .eq('user_id', targetUserId)
              .not('organization_id', 'is', null)
              .limit(1);
            orgId = devOrg && devOrg[0]?.organization_id ? String(devOrg[0].organization_id) : null;
          }
        }

        if (orgId) {
          const { data, error } = await supabase
            .from('screens')
            .select('*')
            .eq('organization_id', orgId)
            .order('created_at', { ascending: false });
          if (error) throw error;
          console.log('📱 useScreens - Fetched screens via org:', data);
          setScreens((data || []).map(screen => ({
            ...screen,
            settings: typeof screen.settings === 'object' && screen.settings !== null ? screen.settings : {}
          })) as any);
        } else {
          // Último recurso: tentar função pública
          const rpc = await supabase.rpc('public_get_my_screens');
          if (rpc.error) {
            console.error('📱 useScreens - RPC public_get_my_screens error:', rpc.error);
            throw rpc.error;
          }
          const data = (rpc.data as any[]) || [];
          console.log('📱 useScreens - Fetched screens via public RPC:', data);
          setScreens(data.map(screen => ({
            ...screen,
            settings: typeof screen.settings === 'object' && screen.settings !== null ? screen.settings : {}
          })) as any);
        }
      }
    } catch (error) {
      console.error('📱 useScreens - Error:', error);
      toast({
        title: "Erro ao carregar telas",
        description: "Não foi possível carregar as telas",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createScreen = async (screenData: Omit<ScreenInsert, 'user_id'>) => {
    if (!user?.id) return null;

    try {
      console.log('📝 useScreens - Criando tela:', screenData);

      // Detectar modo impersonate
      const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
      const impersonateUserId = urlParams?.get('impersonate');
      const isImpersonating = !!impersonateUserId;

      // Verificar se os dados necessários estão presentes
      const desc = typeof screenData.settings === 'object' && screenData.settings !== null ? (screenData.settings as any).description : undefined;
      if (!screenData.name || !desc) {
        throw new Error('Nome e descrição são obrigatórios');
      }

      // Buscar organização do usuário pelo mapeamento organizations_users
      let organization_id = overrideOrganizationId || null;
      if (!organization_id) {
        const { targetUserId: impersonationParam } = getImpersonationContext();
        const localTargetUserId = overrideUserId || impersonationParam || user.id;
        const { data: orgMap, error: orgError } = await supabase
          .from('organizations_users')
          .select('organization_id')
          .eq('user_id', localTargetUserId)
          .limit(1);
        if (orgError) throw orgError;
        if (!orgMap || !orgMap[0]?.organization_id) {
          // Vincular usuário à organização padrão segura
          const defaultOrgId = DEFAULT_ORGANIZATION_ID;
          try {
            await supabase
              .from('organizations_users')
              .insert({ organization_id: defaultOrgId, user_id: localTargetUserId, role: 'admin' });
          } catch { }
          organization_id = defaultOrgId;
        } else {
          organization_id = orgMap[0].organization_id as string;
        }
      }

      let data: any = null;

      if (isImpersonating) {
        // Modo Admin: usar RPC para criar tela
        console.log('👑 useScreens - Modo Admin Impersonate: Criando tela via RPC admin');
        const targetUserId = impersonateUserId || user.id;
        const result = await supabase.rpc('admin_create_screen', {
          p_user_id: targetUserId,
          p_name: screenData.name,
          p_group_id: null,
          p_active: true
        });

        if (result.error) throw result.error;

        const response = result.data as { success: boolean; data?: any; error?: string };
        if (!response.success) {
          throw new Error(response.error || 'Erro ao criar tela via admin');
        }
        data = response.data;
      } else {
        // Modo normal: inserir diretamente
        const result = await supabase
          .from('screens')
          .insert({
            name: screenData.name,
            organization_id,
            settings: screenData.settings
          })
          .select()
          .single();

        if (result.error) throw result.error;
        data = result.data;
      }

      setScreens(prev => [data, ...prev]);
      toast({
        title: "Grupo criado com sucesso!",
        description: `"${data.name}" foi adicionado ao sistema`,
      });

      return data;
    } catch (error) {
      console.error('Erro ao criar tela:', error);
      toast({
        title: "Erro ao criar tela",
        description: "Não foi possível criar a tela",
        variant: "destructive",
      });
      return null;
    }
  };

  const updateScreen = async (id: string, updates: ScreenUpdate) => {
    try {
      console.log('📱 useScreens - Updating screen:', { id, updates });

      // Detectar modo impersonate
      const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
      const impersonateUserId = urlParams?.get('impersonate');
      const isImpersonating = !!impersonateUserId;

      // First get the current screen to preserve settings that weren't updated
      const { data: currentScreen, error: getError } = await supabase
        .from('screens')
        .select('*')
        .eq('id', id)
        .single();

      if (getError) throw getError;

      // Merge existing settings with new settings
      const updatedSettings = {
        ...currentScreen.settings,
        description: typeof updates.settings === 'object' && updates.settings !== null ? (updates.settings as any).description : undefined
      };

      let data: any = null;

      if (isImpersonating) {
        // Modo Admin: usar RPC para atualizar tela
        console.log('👑 useScreens - Modo Admin Impersonate: Atualizando tela via RPC admin');
        const result = await supabase.rpc('admin_update_screen', {
          p_screen_id: id,
          p_name: updates.name || null,
          p_group_id: null,
          p_active: null
        });

        if (result.error) throw result.error;

        const response = result.data as { success: boolean; data?: any; error?: string };
        if (!response.success) {
          throw new Error(response.error || 'Erro ao atualizar tela via admin');
        }
        data = response.data;
      } else {
        // Modo normal: atualizar diretamente
        const result = await supabase
          .from('screens')
          .update({
            name: updates.name,
            settings: updatedSettings,
            updated_at: new Date().toISOString()
          })
          .eq('id', id)
          .select()
          .single();

        if (result.error) throw result.error;
        data = result.data;
      }

      setScreens(prev => prev.map(screen =>
        screen.id === id ? data : screen
      ));

      toast({
        title: "Tela atualizada com sucesso!",
        description: "As configurações da tela foram salvas",
      });

      return data;
    } catch (error) {
      console.error('Erro ao atualizar tela:', error);
      toast({
        title: "Erro ao atualizar tela",
        description: "Não foi possível atualizar a tela",
        variant: "destructive",
      });
      return null;
    }
  };

  const deleteScreen = async (id: string) => {
    try {
      // Detectar modo impersonate
      const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
      const impersonateUserId = urlParams?.get('impersonate');
      const isImpersonating = !!impersonateUserId;

      // Desassociar dispositivos da tela antes de excluir (evitar bloqueios por FK/RLS)
      try {
        await supabase
          .from('devices')
          .update({ screen_id: null })
          .eq('screen_id', id);
      } catch (e) { }

      // Remover referência à tela nas mídias selecionadas
      try {
        const { data: mediasToUpdate } = await supabase
          .from('medias')
          .select('id, selected_screens');
        const affected = (mediasToUpdate || []).filter((m: any) => Array.isArray(m.selected_screens) && m.selected_screens.includes(id));
        for (const media of affected) {
          const newScreens = (media.selected_screens as any[]).filter((s: any) => String(s) !== String(id));
          await supabase
            .from('medias')
            .update({ selected_screens: newScreens })
            .eq('id', media.id);
        }
      } catch (e) { }

      // Remover agendamentos vinculados à tela
      try {
        await supabase
          .from('media_schedules')
          .delete()
          .eq('screen_id', id);
      } catch (e) { }

      if (isImpersonating) {
        // Modo Admin: usar RPC para deletar tela
        console.log('👑 useScreens - Modo Admin Impersonate: Deletando tela via RPC admin');
        const result = await supabase.rpc('admin_delete_screen', {
          p_screen_id: id
        });

        if (result.error) throw result.error;

        const response = result.data as { success: boolean; error?: string };
        if (!response.success) {
          throw new Error(response.error || 'Erro ao deletar tela via admin');
        }
      } else {
        // Modo normal: deletar diretamente
        const { error } = await supabase
          .from('screens')
          .delete()
          .eq('id', id);

        if (error) throw error;
      }

      setScreens(prev => prev.filter(screen => screen.id !== id));
      toast({
        title: "Grupo removido",
        description: "O grupo foi removido com sucesso",
      });

      return true;
    } catch (error) {
      console.error('Erro ao deletar tela:', error);
      toast({
        title: "Erro ao remover grupo",
        description: "Não foi possível remover o grupo",
        variant: "destructive",
      });
      return false;
    }
  };

  const fetchScreensByQuery = async (userId: string, query: string) => {
    try {
      console.log('📱 useScreens - Fetching screens by query:', { userId, query });
      const { targetUserId: impersonationParam } = getImpersonationContext();
      const isImpersonationMode = !!impersonationParam;

      // Se temos organização, filtramos direto no banco
      if (overrideOrganizationId) {
        const { data, error } = await supabase
          .from('screens')
          .select('*')
          .eq('organization_id', overrideOrganizationId)
          .ilike('name', `%${query}%`)
          .order('created_at', { ascending: false });
        if (!error && data) {
          return (data || []).map(screen => ({
            ...screen,
            settings: typeof screen.settings === 'object' && screen.settings !== null ? screen.settings : {}
          }));
        }
      }

      if (isImpersonationMode) {
        const rpc = await supabase.rpc('admin_get_user_screens', { p_user_id: userId });
        if (rpc.error) {
          console.error('📱 useScreens - RPC admin_get_user_screens error:', rpc.error);
          // Fallback por organização do usuário
          const { data: orgMap } = await supabase
            .from('organizations_users')
            .select('organization_id')
            .eq('user_id', userId)
            .limit(1);
          const orgId = orgMap && orgMap[0]?.organization_id;
          if (orgId) {
            const { data } = await supabase
              .from('screens')
              .select('*')
              .eq('organization_id', orgId)
              .ilike('name', `%${query}%`)
              .order('created_at', { ascending: false });
            return (data || []).map(screen => ({
              ...screen,
              settings: typeof screen.settings === 'object' && screen.settings !== null ? screen.settings : {}
            }));
          }
          return [];
        }
        const data = (rpc.data as any[]) || [];
        const filtered = query ? data.filter(s => (s.name || '').toLowerCase().includes(query.toLowerCase())) : data;
        console.log('📱 useScreens - Fetched screens by admin RPC (filtered):', filtered);
        return filtered.map(screen => ({
          ...screen,
          settings: typeof screen.settings === 'object' && screen.settings !== null ? screen.settings : {}
        }));
      } else {
        // Normal mode: resolver organização e filtrar diretamente
        let orgId: string | null = overrideOrganizationId || null;
        if (!orgId) {
          const { data: orgMap } = await supabase
            .from('organizations_users')
            .select('organization_id')
            .eq('user_id', userId)
            .limit(1);
          orgId = orgMap && orgMap[0]?.organization_id ? String(orgMap[0].organization_id) : null;
          if (!orgId) {
            const { data: devOrg } = await supabase
              .from('devices')
              .select('organization_id')
              .eq('user_id', userId)
              .not('organization_id', 'is', null)
              .limit(1);
            orgId = devOrg && devOrg[0]?.organization_id ? String(devOrg[0].organization_id) : null;
          }
        }

        if (orgId) {
          const { data, error } = await supabase
            .from('screens')
            .select('*')
            .eq('organization_id', orgId)
            .ilike('name', `%${query}%`)
            .order('created_at', { ascending: false });
          if (error) {
            console.error('📱 useScreens - Error fetching screens by query:', error);
            return [];
          }
          console.log('📱 useScreens - Fetched screens by org (filtered):', data);
          return (data || []).map(screen => ({
            ...screen,
            settings: typeof screen.settings === 'object' && screen.settings !== null ? screen.settings : {}
          }));
        }

        // Último recurso: função pública
        const rpc = await supabase.rpc('public_get_my_screens');
        if (rpc.error) {
          console.error('📱 useScreens - RPC public_get_my_screens error:', rpc.error);
          return [];
        }
        const data = (rpc.data as any[]) || [];
        const filtered = query ? data.filter(s => (s.name || '').toLowerCase().includes(query.toLowerCase())) : data;
        console.log('📱 useScreens - Fetched screens by public RPC (filtered):', filtered);
        return filtered.map(screen => ({
          ...screen,
          settings: typeof screen.settings === 'object' && screen.settings !== null ? screen.settings : {}
        }));
      }
    } catch (error) {
      console.error('📱 useScreens - Error fetching screens by query:', error);
      toast({
        title: "Erro ao buscar telas",
        description: "Não foi possível buscar as telas",
        variant: "destructive",
      });
      return [];
    }
  };

  useEffect(() => {
    console.log('📱 useScreens - Effect triggered with auth state:', { loading: authLoading, userId: user?.id });
    fetchScreens();
  }, [overrideUserId, user?.id, authLoading]);

  return {
    screens,
    loading: loading || authLoading,
    createScreen,
    updateScreen,
    deleteScreen,
    refreshScreens: fetchScreens,
    fetchScreensByQuery
  };
};
